/* eslint-disable @typescript-eslint/no-unused-vars */

import { AgeingEntity } from "../entities/company/Ageing/AgeingEntity";
import { AIAgentType } from "../entities/company/AIBilling/AIBilling";
import { RequestBackupRequest } from "../entities/company/Backup/RequestBackupRequest";
import { SetBackupScheduleRequest } from "../entities/company/Backup/SetBackupScheduleRequest";
import { CompanySettings } from "../entities/company/Company/CompanySettings";
import { NewCompanyRequest } from "../entities/company/NewCompanyRequest";
import { CompanyAuth } from "../entities/login/CompanyAuth";
import { CompanyToken } from "../entities/login/CompanyToken";
import { UserPayload } from "../entities/login/UserPayload";
import { SmsConfigurationEditModel } from "../pages/company/Configuration/SMSConfigurationEdit";
import ModalService from "../pages/shared/bootstrap/modal/Modal";
import { parseIntOrDefault } from "../utils/ParseUtils";
import { getCache, setCache } from "../utils/PersistenceUtils";
import { handleErrorWithToast } from "../utils/RequestUtils";
import { MapIfEqual } from "../utils/Utils";
import {
  getClientDefaultFieldType,
  getClientFieldIsDefault,
  getClientFieldTitle
} from "./ClientService";
import {
  getInvoiceDefaultFieldType,
  getInvoiceFieldIsDefault,
  getInvoiceFieldTitle
} from "./InvoiceService";
import ProcessService from "./ProcessService";
import RequestService from "./RequestService";
import { TranslationService } from "./TranslationService";

export enum Entities {
  Client = 0,
  Invoice = 1,
  Payment = 2,
  Task = 20,
  Activity,
  ReportCustomerAnalysis,
  Tag,
  Portal
}

export enum DataType {
  Text = 0,
  List = 1,
  Date = 2,
  Number = 3,
  Currency = 4,
  Phone = 5,
  Link = 6,
  PhoneWhatsapp = 7,
  Tags = 20,
  ActivityType,
  Client,
  Priority,
  TextWithOperator,
  ExpirationDate,
  YesNo,
  DaysOld,
  DayCompare,
  Percentage,
  IOStatus,
  Group,
  CurrencyWithCurrencyType,
  Multiselect,
  User,
  Status /*Transaction/Invoice status*/,
  PeriodoGestiones,
  ActivityTypeWithDate,
  Type /* Activity type, but the type, not the ActivityType */,
  Readonly,
  ListNoOperator,
  ClientGroup,
  Download,
  CommentsLink,
  UserMultiSelect
}

const NEEDS_COMPANY_AUTH: Action[] = [
  "newperson",
  "editperson",
  "deleteperson",
  "newtransaction",
  "edittransaction",
  "deletetransaction",
  "newio",
  "editio",
  "deleteio",
  "deletecomment",
  "newwhatsapp",
  "editwhatsapp",
  "deletewhatsapp",
  "deletemail"
];

class CompanyService {
  public static setCompanyAuth(value: CompanyAuth) {
    setCache("company_auth", value);
  }
  public static setCompanyAuthPortal(value: CompanyAuth) {
    setCache("company_auth_portal", value);
  }
  public static getCompanyAuth() {
    return getCache<CompanyAuth>("company_auth");
  }
  public static getCompanyAuthPortal() {
    return getCache<CompanyAuth>("company_auth_portal");
  }
  public static setCompanyToken(value: CompanyToken) {
    setCache("company_token", value);
  }
  public static getCompanyToken() {
    return getCache<CompanyToken>("company_token");
  }
  public static setUserPayload(value: UserPayload) {
    setCache("user_payload", value);
  }
  public static getUserPayload() {
    return getCache<UserPayload>("user_payload");
  }
  public static getUserEmail() {
    return CompanyService.getUserPayload()!.emailaddress;
  }
  // Direcciones que representan al usuario actual al responder un email: la casilla
  // configurada por la empresa (DefaultEmail, desde la que se envía la respuesta) y el
  // email del usuario logueado. Se usan para excluirse de los destinatarios (GDC-5211).
  public static getCurrentUserEmails() {
    const emails: string[] = [];

    const defaultMail = CompanyService.getCompanyAuth()?.DefaultEmail?.trim();
    if (defaultMail) {
      const match = defaultMail.match(/<(.*?)>/);
      emails.push((match?.[1] || defaultMail).trim());
    }

    const userEmail = CompanyService.getUserPayload()?.emailaddress?.trim();
    if (userEmail) {
      emails.push(userEmail);
    }

    return emails;
  }
  public static getUserFullName() {
    return CompanyService.getUserPayload()!.fullname;
  }
  public static getConfigItems() {
    return CompanyService.getCompanyAuth()!.configItems;
  }
  public static getUserCurrentVersion() {
    return CompanyService.getCompanyAuth()!.currentVersion;
  }
  public static getAdditionalDefinitions() {
    return CompanyService.getCompanyAuth()!.additionalDefinitions;
  }
  public static getAdditionalDefinitionsPortal() {
    return CompanyService.getCompanyAuthPortal()!.additionalDefinitions;
  }
  public static getUsers() {
    return CompanyService.getCompanyAuth()!.users;
  }
  public static getTags() {
    return CompanyService.getCompanyAuth()!.tags;
  }
  public static getEmailTemplates() {
    return CompanyService.getCompanyAuth()!.emailTemplates;
  }
  public static getEmailFrames() {
    return CompanyService.getCompanyAuth()!.emailFrames;
  }
  public static getActivityTypes() {
    return CompanyService.getCompanyAuth()!.activityTypes;
  }
  public static getIOGroup() {
    return CompanyService.getCompanyAuth()!.IOGroup;
  }
  public static getGroupName() {
    return CompanyService.getCompanyAuth()!.groupName;
  }
  public static getIoStatus() {
    return [
      {
        IOStatusDependencies: [],
        IOStatusID: 0,
        Value: CompanyService.getNormalIOStatusName(),
        Exclude: false,
        HasDependency: false,
        PublicValue: CompanyService.getNormalIOStatusName(),
        IOStatusID_ref: null
      },
      ...CompanyService.getCompanyAuth()!.iostatus
    ];
  }
  public static getAllowedInvoiceStatusIds(): number[] | undefined {
    return CompanyService.getCompanyAuth()?.AllowStatusIds;
  }
  public static getAllowedInvoiceStatuses() {
    const allStatuses = CompanyService.getIoStatus();
    const allowedIds = CompanyService.getAllowedInvoiceStatusIds();

    // Si AllowStatusIds es undefined o array vacío, retornar todos los estados
    if (!allowedIds || allowedIds.length === 0) {
      return allStatuses;
    }

    // Filtrar solo los estados permitidos
    return allStatuses.filter((status) =>
      allowedIds.includes(status.IOStatusID)
    );
  }
  public static getSegments() {
    return CompanyService.getCompanyAuth()!.segments!.map((x) => ({
      Id: x.Id,
      Value: MapIfEqual(x.Value, "", "\u00A0")
    }))!;
  }
  public static getCompanyId() {
    return CompanyService.getCompanyAuth()!.companyGuid;
  }
  public static getStoredFilters() {
    return CompanyService.getCompanyAuth()?.storedFilters;
  }
  public static getIoSortedFields() {
    return CompanyService.getCompanyAuth()!.ioSortedFields;
  }
  public static getTransactionTypes() {
    return CompanyService.getCompanyAuth()!.transactionTypes;
  }
  public static getCurrencies() {
    return CompanyService.getCompanyAuth()!.currencies;
  }
  public static getDefaultCurrencyId() {
    return CompanyService.getCompanyAuth()!.defaultCurrencyId;
  }
  public static getReportExports() {
    return CompanyService.getCompanyAuth()!.reportExports;
  }
  public static getCompanyName() {
    return CompanyService.getCompanyAuth()!.companyName;
  }
  public static getPersonSortedFields() {
    return CompanyService.getCompanyAuth()!.personSortedFields;
  }
  public static getEnabledSegmentPriorities() {
    return CompanyService.getCompanyAuth()!.EnabledSegmentPriorities;
  }
  public static getTotalPriorities() {
    return CompanyService.getCompanyAuth()!.TotalPriorities;
  }
  public static getDashboardPanelId() {
    return CompanyService.getCompanyAuth()!.dashBoardPanelId;
  }
  public static getNormalIOStatusName() {
    return CompanyService.getCompanyAuth()!.NormalIOStatusName ?? "Normal";
  }
  public static canShowReceipt() {
    return CompanyService.getCompanyAuth()!.HasReceipt;
  }
  public static canShowSMS() {
    return CompanyService.getCompanyAuth()!.showSMS;
  }
  public static canShowWhatsApp() {
    return CompanyService.getCompanyAuth()!.Whatsapp;
  }

  // La empresa tiene habilitado el modulo Cashapp Y el usuario tiene el switch
  // prendido. El backend ya combina ambas condiciones en el claim `cashapp`.
  public static canShowCashApp() {
    return CompanyService.getCompanyToken()?.cashapp === "1";
  }

  /**
   * Indica si el usuario puede operar WhatsApp: la empresa lo tiene habilitado
   * y el usuario tiene permiso de alta o edicion.
   * Gate unico para el icono de WhatsApp del menu y del inbox.
   */
  public static canUseWhatsapp(): boolean {
    if (!CompanyService.getCompanyToken() || !CompanyService.getCompanyAuth()) {
      return false;
    }
    return (
      CompanyService.canShowWhatsApp() &&
      (CompanyService.canDo("newwhatsapp") ||
        CompanyService.canDo("editwhatsapp"))
    );
  }

  /**
   * Indica si el usuario puede recibir notificaciones de WhatsApp del chatbot:
   * ademas de poder operar WhatsApp, la empresa debe tener el chatbot activo.
   * Gate unico para el socket de notificaciones, el panel flotante y el
   * indicador de disponibilidad del header.
   */
  public static canUseWhatsappNotifications(): boolean {
    return (
      CompanyService.canUseWhatsapp() &&
      CompanyService.getSetting("allowwhatsappchatbot")
    );
  }
  public static canShowPaymentProvider(): boolean {
    return CompanyService.getCompanyAuth()!.AllowPaymentProvider;
  }
  public static canShowSuperSegment(): boolean {
    return CompanyService.getCompanyAuth()!.AllowSuperSegment;
  }

  public static canEditTemplate(): boolean {
    return CompanyService.getCompanyAuth()!.AllowTemplateEdition;
  }

  public static hasDeletedUsersFilter(): boolean {
    return CompanyService.getCompanyAuth()?.DeletedUsersFilter === true;
  }

  public static canShowAgentAI(agentType: AIAgentType): boolean {
    if (!agentType) return false;

    const allowedAgents = CompanyService.getCompanyAuth()?.AllowsAgentsAI;

    return Array.isArray(allowedAgents) && allowedAgents.includes(agentType);
  }

  public static canShowFtpEnabled() {
    return CompanyService.getCompanyAuth()!.FtpEnabled;
  }
  public static getBlock() {
    return CompanyService.getCompanyAuth()?.block;
  }
  public static getExpire() {
    return CompanyService.getCompanyAuth()?.expire;
  }
  public static getPersonKey() {
    return CompanyService.getCompanyAuth()!.PersonKey;
  }
  public static getCustomerFieldName() {
    return CompanyService.getCompanyAuth()?.customerFieldName || "";
  }
  public static getCustomersFieldName() {
    return CompanyService.getCompanyAuth()?.customersFieldName || "";
  }
  public static getVersionAllowed() {
    return CompanyService.getCompanyAuth()!.versionAllowed;
  }
  public static getTimeZoneID() {
    return CompanyService.getCompanyAuth()!.timezoneid;
  }
  public static setConfigCompany(
    companySettings: CompanySettings
  ): Promise<unknown | Error> {
    return RequestService.post("/company/setconfig", companySettings);
  }
  public static getConfigsCompany() {
    return RequestService.post("/company/getconfigs", {});
  }
  public static setSmsConfig(request: SmsConfigurationEditModel) {
    return RequestService.post("/company/savesmsconfig", request);
  }
  public static getSmsConfig(): Promise<
    { item: { Name: string; Value: string }[] } | Error
  > {
    return RequestService.get("/company/getsmsconfig", {});
  }
  public static setBackupSchedule(request: SetBackupScheduleRequest) {
    return RequestService.post("/company/setbackupschedule", request);
  }
  public static requestBackup(request: RequestBackupRequest) {
    return RequestService.post("/company/setbackuprequest", request);
  }
  /**
   * @returns the timezone offset in minutes
   */
  public static getTimezone() {
    return parseIntOrDefault(
      CompanyService.getCompanyToken()!.timezone,
      new Date().getTimezoneOffset()
    );
  }
  public static getTimezoneID() {
    return CompanyService.getCompanyToken()!.timezoneid;
  }
  public static getWhatsappUrl(phone: string) {
    if (!phone) {
      return undefined;
    }
    const regex = /[+\-\s]/gi;
    return "https://api.whatsapp.com/send?phone=" + phone.replaceAll(regex, "");
    //return "/es/mail?url=" + encodeURIComponent(whatsappUrl);
  }

  public static getPhoneLink(phone: string) {
    if (!phone) {
      return "";
    }
    if (phone[0] !== "+") {
      phone = "+" + phone;
    }
    return phone;
  }

  public static getAdditionalDefinitionsFiltered = (
    entityType: Entities,
    right?: boolean,
    manualEdit?: boolean,
    hidden: boolean = false
  ) => {
    let result = CompanyService.getAdditionalDefinitions().filter(
      (x) => x.Entity === entityType
    );
    if (right !== undefined) {
      result = result.filter((x: any) => {
        return (x.Right ?? false) === right;
      });
    }

    if (hidden) {
      result = result.filter((x) => !(x?.Hidden ?? false));
    }

    if (manualEdit !== undefined) {
      result = result.filter((x) => x.ManualEdit === manualEdit);
    }
    return result;
  };

  public static getConfigItemsFiltered = (
    entityType: Entities,
    includeInCustomerPortal?: boolean,
    includeValues?: number[]
  ) => {
    let result = CompanyService.getConfigItems().filter(
      (x) => x.Entity === entityType
    );
    if (includeInCustomerPortal !== undefined) {
      result = result.filter(
        (x) =>
          x.IncludeInCustomerPortal === includeInCustomerPortal ||
          includeValues?.includes(x.field)
      );
    }
    return result;
  };

  public static setTags(tags: CompanyAuth.Tag[]) {
    return CompanyService.setCompanyAuth({
      ...CompanyService.getCompanyAuth()!,
      tags
    });
  }
  public static getEntityDefaultFieldType = (
    entityType: Entities,
    id: number
  ) => {
    switch (entityType) {
      case Entities.Client:
        return getClientDefaultFieldType(id);
      case Entities.Invoice:
        return getInvoiceDefaultFieldType(id);
      default:
        return DataType.Text;
    }
  };
  public static getEntityFieldIsDefault = (
    entityType: Entities,
    id: number
  ) => {
    switch (entityType) {
      case Entities.Client:
        return getClientFieldIsDefault(id);
      case Entities.Invoice:
        return getInvoiceFieldIsDefault(id);
      default:
        return id < 0;
    }
  };
  public static getEntityFieldTitle = (entityType: Entities, id: number) => {
    switch (entityType) {
      case Entities.Client:
        return getClientFieldTitle(id);
      case Entities.Invoice:
        return getInvoiceFieldTitle(id);
      default:
        return undefined;
    }
  };
  public static getAccountstatus() {
    return CompanyService.getCompanyToken()!.accountstatus;
  }
  public static getAuthorization() {
    return CompanyService.getCompanyToken()!.authorization;
  }
  public static getCompanyAuthorization() {
    return CompanyService.getCompanyToken()!.companyauthorization;
  }

  /**
   * Verifica si un elemento existe exactamente en una lista separada por comas
   * Evita falsos positivos cuando un elemento es subcadena de otro (ej: "editio" vs "editiostatus")
   */
  private static hasExactItem(list: string, item: string): boolean {
    if (!list || !item) return false;
    // Verificar que el item esté como elemento completo, no como subcadena
    // Buscar: inicio de string o coma, seguido del item exacto, seguido de coma o fin de string
    const regex = new RegExp(
      `(^|,)${item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(,|$)`
    );
    return regex.test(list);
  }

  private static hasAnyExactItem(list: string, items: string[]): boolean {
    if (!list || !items || items.length === 0) return false;
    const listItems = new Set(list.split(","));
    return items.some((item) => listItems.has(item));
  }

  public static getIsAdmin(): boolean {
    return CompanyService.getCompanyToken()!.isadmin === "1";
  }

  public static getIsOwner(): boolean {
    return (
      CompanyService.getCompanyToken()!.userid ===
      CompanyService.getCompanyToken()!.ownerId
    );
  }

  public static getIsReadonly(): boolean {
    return CompanyService.getCompanyToken()!.isreadonly === "1";
  }
  public static getIsInvited(): boolean {
    return !CompanyService.getIsAdmin() && !CompanyService.getIsReadonly();
  }

  /**
   * Plantillas de reportes (report export): solo admin u owner. Queda oculto
   * tanto para "Solo Consulta" como para "Invitado". GDC-5198.
   */
  public static canEditReportExport(): boolean {
    if (CompanyService.isProductionMode()) {
      return false;
    }
    return CompanyService.getIsAdmin() || CompanyService.getIsOwner();
  }

  /**
   * Reportes programados: no hay una Action especifica en el union `Action`, y
   * `viewreport` es justamente la que tiene un "Solo Consulta". GDC-5198.
   */
  public static canEditScheduledReport(): boolean {
    if (CompanyService.isProductionMode()) {
      return false;
    }
    return !CompanyService.getIsReadonly();
  }

  /**
   * Agenda editing: denied in production mode and for "Solo Consulta" users.
   */
  public static canEditAgenda(): boolean {
    return (
      !CompanyService.isProductionMode() && !CompanyService.getIsReadonly()
    );
  }

  /**
   * Agent monitor (inbox): admin only, and never in production mode.
   */
  public static canMonitorAgents(): boolean {
    return !CompanyService.isProductionMode() && CompanyService.getIsAdmin();
  }

  /**
   * Pin/unpin activities: denied in production mode.
   */
  public static canPinActivity(): boolean {
    return !CompanyService.isProductionMode();
  }

  /**
   * Mark/unmark tasks as favorite: denied in production mode.
   */
  public static canFavTask(): boolean {
    return !CompanyService.isProductionMode();
  }

  /**
   * Message actions (reply, reply all, forward, AI assistant, mark as unread,
   * selection for bulk actions): denied in production mode.
   */
  public static canActOnMessage(): boolean {
    return !CompanyService.isProductionMode();
  }

  /**
   * Saved report groupings (save, edit, delete): denied in production mode.
   */
  public static canManageSavedGroupings(): boolean {
    return !CompanyService.isProductionMode();
  }
  public static getUserid() {
    return CompanyService.getCompanyToken()!.userid;
  }
  public static getHasSegments() {
    return CompanyService.getCompanyToken()!.hassegments === "1";
  }
  public static getFilterUserIdPaused() {
    return CompanyService.getCompanyToken()!.filteruseridpaused === "1";
  }
  public static getCompanies() {
    return CompanyService.getCompanyAuth()!.companies;
  }

  /**
   * Se usa para decidir si mostrar el acceso al módulo Consolidado.
   * Reglas:
   * - El company auth debe tener más de 1 company en companies
   */
  public static canViewConsolidated(): boolean {
    const auth = CompanyService.getCompanyAuth();
    const allowFlag = auth?.ViewConsolidate === true; // viene de company_cache_company_auth
    const companiesCount = auth?.companies?.length ?? 0;
    return allowFlag && companiesCount > 1;
  }

  /**
   * Indica si el usuario ya configuró al menos una empresa para consolidar.
   * Viene de company_auth (IsConsolidatedSetted). Si es false, hay que
   * redirigir a la configuración del Consolidado en vez de mostrar Dashboard/Clientes.
   */
  public static isConsolidatedSetted(): boolean {
    return CompanyService.getCompanyAuth()?.IsConsolidatedSetted === true;
  }

  public static canEditCompanySettings(): boolean {
    // Un "Solo Consulta" no accede a la configuracion de empresa aunque el
    // backend haya persistido flags residuales en su authorization. GDC-5198.
    if (CompanyService.isProductionMode() || CompanyService.getIsReadonly()) {
      return false;
    }
    return (
      this.getIsAdmin() ||
      this.getIsOwner() ||
      this.hasAnyExactItem(CompanyService.getAuthorization(), [
        "setfieldsperson",
        "setfieldsio",
        "setfieldstransaction",
        "editiostatus",
        "edittransactiontype",
        "settemplatewhatsapp",
        "settemplateemail",
        "settemplatesms",
        "setpayment",
        "editrole",
        "edituser",
        "editactivitytype",
        "viewimportlog",
        "setimport",
        "editdashboard",
        "setaiassistant",
        "setcompany",
        "setcompanyothersettings"
      ])
    );
  }

  /**
   * Production mode: a back office user (impersonating in the production
   * environment) acting on a company flagged with the `production` token claim.
   */
  private static isProductionMode(): boolean {
    return (
      CompanyService.isBackOfficeUser() &&
      CompanyService.getCompanyToken()?.production === "1"
    );
  }

  public static canDo(action: Action) {
    if (CompanyService.getAccountstatus() !== "0") {
      return false;
    }
    if (CompanyService.isProductionMode()) {
      //ONLY PERMISSION TO CHECK FOR Production IS viewing reports
      if (action === "viewreport") {
        return CompanyService.hasExactItem(
          CompanyService.getAuthorization(),
          action
        );
      }
      return false;
    }
    if (CompanyService.getIsAdmin()) {
      return true;
    }
    if (
      NEEDS_COMPANY_AUTH.includes(action) &&
      !CompanyService.hasExactItem(
        CompanyService.getCompanyAuthorization(),
        action
      )
    ) {
      return false;
    }
    if (CompanyService.getIsReadonly()) {
      //ONLY PERMISSION TO CHECK FOR A READONLY USER IS EXPORT
      if (action === "export" || action === "viewreport") {
        return CompanyService.hasExactItem(
          CompanyService.getAuthorization(),
          action
        );
      }
      return false;
    }
    return CompanyService.hasExactItem(
      CompanyService.getAuthorization(),
      action
    );
  }

  public static canDoAny(actions: Action[]) {
    return actions.some((action) => CompanyService.canDo(action));
  }

  public static GetDefaultCurrencySymbol() {
    const currencyId = CompanyService.getDefaultCurrencyId();
    return CompanyService.getCurrencies().find(
      (x) => x.CurrencyId === currencyId
    )!.Symbol;
  }

  public static GetAgeingValues() {
    const ageing: AgeingEntity[] = [];
    const agValues = [
      "due3",
      "due23",
      "due12",
      "due1",
      "nodue1",
      "nodue12",
      "nodue23",
      "nodue3"
    ];
    const ageingValues = CompanyService.getCompanyAuth()!.GraphScales;

    ageing.push({
      due: true,
      name: TranslationService.getTranslation("Aging-MoreXDaysAgo").replace(
        "{0}",
        (-ageingValues[0]).toString()
      ),
      maxDays: ageingValues[0],
      filterVal: "due3"
    });

    ageingValues.forEach((val, i) => {
      if (i + 1 >= ageingValues.length) {
        return;
      }
      const nextVal = ageingValues[i + 1];
      const due = val < 0;
      let name = "";
      let filterVal = agValues[i + 1];

      if (nextVal < 0) {
        name = TranslationService.getTranslation("Aging-MoreXYDays")
          .replace("{0}", (-(nextVal - 1)).toString())
          .replace("{1}", (-val).toString());
      } else if (nextVal === 0) {
        name = TranslationService.getTranslation("Aging-LastXDays").replace(
          "{0}",
          (-val).toString()
        );
      } else if (val === 0) {
        name = TranslationService.getTranslation("Aging-LessXDays").replace(
          "{0}",
          nextVal.toString()
        );
      } else {
        name = TranslationService.getTranslation("Aging-InXYDays")
          .replace("{0}", val.toString())
          .replace("{1}", (nextVal - 1).toString());
      }

      ageing.push({
        due,
        name,
        minDays: val,
        maxDays: nextVal,
        filterVal
      });
    });

    ageing.push({
      due: false,
      name: TranslationService.getTranslation("Aging-MoreXDays").replace(
        "{0}",
        ageingValues[ageingValues.length - 1].toString()
      ),
      minDays: ageingValues[ageingValues.length - 1],
      filterVal: "nodue3"
    });

    return ageing;
  }

  public static changeCompanyName(name: string) {
    return RequestService.post("/company/setname", { name });
  }
  public static changeCompanyLogo(fileId: string) {
    if (fileId) {
      return RequestService.post("/company/setlogo", { fileId });
    } else {
      return RequestService.post("/company/clearLogo", {});
    }
  }
  public static getSetting(key: KeysToLower<CompanySettings>) {
    return CompanyService.hasExactItem(
      CompanyService.getCompanyAuthorization(),
      key
    );
  }
  public static newCompany(
    request: NewCompanyRequest
  ): Promise<Error | { Id: string }> {
    return RequestService.post("/company/new", request);
  }
  public static deleteCompany() {
    const deleteCompany = async () => {
      handleErrorWithToast(RequestService.post("/company/delete", {}));
      window.location.href = `/${TranslationService.currentLanguage}/switchcompany?to=${CompanyService.getCompanies()[0].Id}`;
    };
    ModalService.showDefaultModal({
      acceptButtonClassName: "btn-danger btn",
      acceptButtonLabel: TranslationService.translate.Delete,
      message: TranslationService.translate.DeleteCompanyMessage,
      title: TranslationService.translate.Delete,
      onAcceptClick: deleteCompany
    });
  }
  public static resetAll() {
    const resetAll = async () => {
      await ProcessService.showResultToast(
        RequestService.post("/company/resetall", {})
      );
    };
    ModalService.showDefaultModal({
      acceptButtonClassName: "btn btn-danger",
      acceptButtonLabel: TranslationService.translate.ResetCompany,
      message: TranslationService.translate.ResetAllMessage,
      title: TranslationService.translate.Clear,
      onAcceptClick: resetAll
    });
  }
  public static resetIo() {
    const resetIo = async () => {
      await ProcessService.showResultToast(
        RequestService.post("/company/resetio", {})
      );
    };
    ModalService.showDefaultModal({
      acceptButtonClassName: "btn btn-danger",
      acceptButtonLabel: TranslationService.translate.Clear,
      message: TranslationService.translate.ResetIoMessage,
      title: TranslationService.translate.Clear,
      onAcceptClick: resetIo
    });
  }

  public static isBackOfficeUser(): boolean {
    const hostname = window.location.hostname;
    const isProduction = hostname === "www2.intiza.com";

    if (!isProduction) return true;

    return (CompanyService.getCompanyToken()?.adminid ?? "") !== "";
  }

  public static inHouse(): boolean {
    return CompanyService.getCompanyToken()!.inhouse === "1";
  }
}
type KeysToLower<T extends Record<string, unknown>> = keyof T extends string
  ? Lowercase<keyof T>
  : never;
export type Action =
  | "allowsegmentfiltertoggle"
  | "checkotherstask"
  | "checktask"
  | "deletecomment"
  | "deletecompany"
  | "deleteio"
  | "deleteperson"
  | "deletetag"
  | "deletetask"
  | "deletetransaction"
  | "deleteuser"
  | "editaction"
  | "editadditionaldata"
  | "editcomment"
  | "editcompany"
  | "editcurrency"
  | "editgoal"
  | "editio"
  | "editiostatus"
  | "editmail"
  | "editperson"
  | "editsegment"
  | "edittag"
  | "edittask"
  | "edittransaction"
  | "edituser"
  | "enabled"
  | "export"
  | "haspassword"
  | "importdata"
  | "newcomment"
  | "newcompany"
  | "newio"
  | "newmail"
  | "newperson"
  | "newsms"
  | "newtag"
  | "newtask"
  | "newtransaction"
  | "newuser"
  | "reporting"
  | "sync"
  | "viewreport"
  | "hasReceipt"
  | "newwhatsapp"
  | "editwhatsapp"
  | "deletewhatsapp"
  | "deletemail"
  | "setfieldsperson"
  | "setfieldsio"
  | "setfieldstransaction"
  | "edittransactiontype"
  | "settemplatewhatsapp"
  | "settemplateemail"
  | "settemplatesms"
  | "setpayment"
  | "setcompany"
  | "setcompanyothersettings"
  | "editrole"
  | "editactivitytype"
  | "viewimportlog"
  | "setimport"
  | "editdashboard"
  | "setaiassistant"
  | "changeiostatus"
  | "editimporttemplate"
  | "viewuser"
  | "viewrole"
  | "viewaction"
  | "usemcp";
export default CompanyService;
